import { getDeviceType } from "@b2bportal/core-utilities";
import { Event } from "@checkout/events";
import { parseFlightsQueryString } from "@checkout/states";
import { CheckoutQueryParams, ParentState } from "@checkout/types";
import type * as H from "history";
import queryStringParser from "query-string";
import type { StateValue, send as xStateSend } from "xstate";

import { stringifyParentStateValue } from "../flights/utilities";
import type { FlightExchangeMachineContext } from "./types";

const flightExchangeStateToTransitionEventMap = {
  [ParentState.bookingConfirmation]: Event.GO_TO_REVIEW,
  [ParentState.cardPayment]: Event.GO_TO_CARD_PAYMENT,
  [ParentState.review]: Event.GO_TO_REVIEW,
};

// Map of states that should not transition when query params change
// Map: { state that should not transition => previous tracked state }
// Eg: if user is in "cartQuote" and hits back on their browser, the user should not be allowed to go back 1 step and
// instead stay in the current step till it resolves
const preventTransitionStates = {
  [ParentState.cartQuote]: ParentState.review,
  [ParentState.cartUpdate]: ParentState.review,
  [ParentState.cartFulfill]: ParentState.review,
};

const untrackedStates: StateValue[] = [
  ParentState.cartFulfill,
  ParentState.cartQuote,
  ParentState.cartUpdate,
  ParentState.loading,
];

export const populateCheckoutQueryParams = (
  state: StateValue,
  history: H.History
) => {
  const parsedQuery = parseFlightsQueryString(history);
  const shouldUpdateQuery = !untrackedStates.includes(state);
  const nextState = stringifyParentStateValue(state);
  const { isDesktopAndUp } = getDeviceType();

  if (isDesktopAndUp && nextState !== ParentState.bookingConfirmation) {
    return;
  }

  //  If xstate state value is not in query params or if it conflicts with what the
  //  current query params contains, then we push new query params to the URL

  const historyData = {
    pathname: history.location.pathname,
    search: queryStringParser.stringify({
      ...parsedQuery,
      [CheckoutQueryParams.checkoutState]: nextState,
    }),
    state: history.location.state,
  };
  const { checkoutState: currentState } = parsedQuery;

  if (!currentState && shouldUpdateQuery) {
    history.replace(historyData);
  } else if (currentState && nextState !== currentState && shouldUpdateQuery) {
    history.push(historyData);
  }
};

export const transitionStateOnPathnameChange = (
  state: StateValue,
  history: H.History,
  send: typeof xStateSend
) => {
  const parsedQuery = parseFlightsQueryString(history);
  const nextState = stringifyParentStateValue(state);
  const { checkoutState: currentState } = parsedQuery;
  const preventedTarget = preventTransitionStates[nextState];
  const isTransitionBlocked = !!preventedTarget;
  const allowTransition =
    !isTransitionBlocked &&
    currentState &&
    !untrackedStates.includes(currentState);
  const hasSucceededFulfillment = nextState === ParentState.bookingConfirmation;

  if (currentState && nextState !== currentState && allowTransition) {
    if (hasSucceededFulfillment) {
      history.push("/");
    } else {
      send({
        type: flightExchangeStateToTransitionEventMap[currentState],
      });
    }
  } else if (isTransitionBlocked && preventedTarget !== currentState) {
    const historyData = {
      pathname: history.location.pathname,
      search: queryStringParser.stringify({
        ...parsedQuery,
        [CheckoutQueryParams.checkoutState]: preventedTarget,
      }),
      state: history.location.state,
    };

    // Pushing the same stack that was removed when the user goes 1 step back in their browser
    history.push(historyData);
  }
};

export const validateContext = (ctx: FlightExchangeMachineContext) =>
  Boolean(
    ctx.exchangeContext.exchangePolicy &&
      ctx.exchangeContext.originalItinerary &&
      ctx.flightShop.selectedTrip.outgoingFareId &&
      ctx.flightShop.selectedTrip.outgoingSliceId &&
      ctx.flightShop.selectedTrip.tripId
  );
